import { z } from 'zod';
import { PAGE_ARCHETYPES, type PageArchetype } from './design.ts';

/**
 * DUPLO-max block composition — the catalog + schema + validator.
 *
 * This module is the *contract* for composable pages. It is pure data +
 * pure functions (NO React) so it can be imported by:
 *   - apps/admin   (build the block tray + editor forms + live validation)
 *   - @h2m/generator / @h2m/wireframes (render the tree)
 *   - apps/build-workflow / api-worker (validate + render on save)
 *
 * Mental model (see CLAUDE.md DUPLO discussion):
 *   Skin   = whole-site look (chosen once, enum-locked).
 *   Block  = a big self-contained section variant (a brick). Renders only
 *            through @h2m/wireframes with skin tokens → AAA-safe by
 *            construction.
 *   Slot   = a typed content hole in a block (the studs). Filled by the
 *            customer, or auto-bound to clinic data from the brief.
 *
 * A page is an ordered (and, for container blocks, nested) tree of
 * BlockInstance. The customer can add/remove/reorder/nest blocks and fill
 * slots — but every legal arrangement is design-system-safe because the
 * only verbs are constrained by the manifest + validateComposition().
 */

/* ───────────────────────────── categories ───────────────────────────── */

export const BLOCK_CATEGORIES = [
  'structure', // nav / footer — locked, one each, fixed position
  'hero',
  'trust', // proof / stats / logos / accreditations
  'services',
  'team',
  'reviews',
  'about',
  'contact',
  'cta',
  'booking',
  'editorial',
  'conversion',
  'layout', // container blocks (columns) — DUPLO-max nesting
] as const;
export type BlockCategory = (typeof BLOCK_CATEGORIES)[number];

/* ────────────────────────── field descriptors ───────────────────────── */

/**
 * A typed content slot. Drives BOTH the editor form control and the
 * per-field validation in validateComposition(). There is deliberately no
 * "color" / "spacing" / "font" / "raw html" field type — design is the
 * skin's job, not the customer's.
 */
export const FIELD_TYPES = [
  'text', // single line
  'textarea', // multi-line plain text
  'number',
  'boolean',
  'enum',
  'image', // media-library asset id / url
  'url', // href (internal path or external)
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export interface FieldDescriptor {
  key: string;
  /** Polish label shown in the editor. */
  label: string;
  type: FieldType;
  /** Polish help text. */
  hint?: string;
  optional?: boolean;
  /** text/textarea max length (chars). */
  maxLength?: number;
  /** number bounds. */
  min?: number;
  max?: number;
  /** enum options — value is stored, label (PL) is shown. */
  options?: Array<{ value: string; label: string }>;
}

/* ───────────────────────── manifest (the catalog) ────────────────────── */

/**
 * One brick type. `component` is the @h2m/wireframes export name the render
 * map binds to; this module never imports it (keeps React out of the
 * schema package).
 */
export interface BlockManifestEntry {
  id: string; // e.g. 'hero.media-split' — category.variant
  category: BlockCategory;
  /** @h2m/wireframes export name the render map resolves. */
  component: string;
  /** Polish display name + one-line description for the tray. */
  name: string;
  description: string;
  /** Which page archetypes may use this block. 'all' = every archetype. */
  archetypes: PageArchetype[] | 'all';
  /** Per-page count bounds for this block's *category*. */
  cardinality: { min: number; max: number };
  /** Must sit at the top / bottom of the content stack. */
  position?: 'top' | 'bottom' | 'any';
  /**
   * Mutual-exclusion group. At most one block whose entry shares the same
   * exclusiveGroup may appear on a page. Encodes the audit's
   * "no stacked trust strips — pick one" rule deterministically.
   */
  exclusiveGroup?: string;
  /** Customer cannot delete/move (nav, footer). */
  locked?: boolean;
  /** Auto-bound clinic data this block reads from the brief fixture. */
  dataBinding?: 'services' | 'doctors' | 'reviews' | 'hours' | 'faq' | 'accreditations';
  /**
   * Gating: minimum brief data for the block to render meaningfully. Keys
   * are logical ('doctorPhotos', 'reviews', 'doctors'). Surfaced as a
   * warning in the editor + skipped at render when unmet (the photo gate).
   */
  minRequirements?: Partial<Record<'doctorPhotos' | 'reviews' | 'doctors' | 'services', number>>;
  /** Container blocks: which child categories they accept. */
  acceptsChildren?: BlockCategory[];
  /** Editable content slots (the studs). */
  fields: FieldDescriptor[];
  /** Seed content when the block is first dropped. */
  defaultContent: Record<string, unknown>;
}

// Common CTA field cluster reused across hero/cta blocks.
const CTA_FIELDS: FieldDescriptor[] = [
  { key: 'primaryCtaLabel', label: 'Przycisk główny', type: 'text', maxLength: 30, optional: true },
  { key: 'primaryCtaHref', label: 'Link przycisku głównego', type: 'url', maxLength: 200, optional: true },
  { key: 'secondaryCtaLabel', label: 'Przycisk drugi', type: 'text', maxLength: 30, optional: true },
];

const COLS_FIELD: FieldDescriptor = {
  key: 'cols',
  label: 'Kolumny',
  type: 'enum',
  optional: true,
  options: [
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
  ],
};

export const BLOCK_MANIFEST: BlockManifestEntry[] = [
  /* ── structure (locked) ── */
  {
    id: 'nav.standard',
    category: 'structure',
    component: 'TopNav',
    name: 'Nawigacja',
    description: 'Górne menu z logo i przyciskiem umawiania.',
    archetypes: 'all',
    cardinality: { min: 1, max: 1 },
    position: 'top',
    locked: true,
    fields: [
      { key: 'primaryCtaLabel', label: 'Przycisk w menu', type: 'text', maxLength: 24, optional: true },
    ],
    defaultContent: { primaryCtaLabel: 'Umów wizytę' },
  },
  {
    id: 'footer.standard',
    category: 'structure',
    component: 'Footer',
    name: 'Stopka',
    description: 'Stopka z danymi kliniki, linkami i NIP.',
    archetypes: 'all',
    cardinality: { min: 1, max: 1 },
    position: 'bottom',
    locked: true,
    fields: [],
    defaultContent: {},
  },

  /* ── hero (one per page, top of content) ── */
  {
    id: 'hero.media-split',
    category: 'hero',
    component: 'HeroMediaSplit',
    name: 'Hero ze zdjęciem (split)',
    description: 'Nagłówek + tekst po lewej, zdjęcie po prawej.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    position: 'top',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'headline', label: 'Nagłówek', type: 'text', maxLength: 90 },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
      ...CTA_FIELDS,
      { key: 'mediaSrc', label: 'Zdjęcie', type: 'image', optional: true },
      { key: 'mediaAlt', label: 'Opis zdjęcia (alt)', type: 'text', maxLength: 120, optional: true },
    ],
    defaultContent: {
      headline: 'Opieka, której można zaufać.',
      lead: 'Nowoczesna klinika blisko Ciebie. Umów wizytę online w kilka sekund.',
      primaryCtaLabel: 'Umów wizytę',
      primaryCtaHref: '#kontakt',
    },
  },
  {
    id: 'hero.centered',
    category: 'hero',
    component: 'HeroCentered',
    name: 'Hero wyśrodkowany',
    description: 'Wyśrodkowany nagłówek bez dużego zdjęcia.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    position: 'top',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'headline', label: 'Nagłówek', type: 'text', maxLength: 90 },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
      ...CTA_FIELDS,
    ],
    defaultContent: { headline: 'Twoje zdrowie w dobrych rękach.', primaryCtaLabel: 'Umów wizytę', primaryCtaHref: '#kontakt' },
  },
  {
    id: 'hero.with-stats',
    category: 'hero',
    component: 'HeroWithStats',
    name: 'Hero ze statystykami',
    description: 'Nagłówek + pasek liczb (lata, pacjenci, ocena).',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    position: 'top',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'headline', label: 'Nagłówek', type: 'text', maxLength: 90 },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
      ...CTA_FIELDS,
    ],
    defaultContent: { headline: 'Doświadczenie, które widać w liczbach.', primaryCtaLabel: 'Umów wizytę', primaryCtaHref: '#kontakt' },
  },
  {
    id: 'hero.emergency',
    category: 'hero',
    component: 'HeroEmergency',
    name: 'Hero — dyżur 24h',
    description: 'Mocny nagłówek z telefonem alarmowym.',
    archetypes: ['emergency', 'landing'],
    cardinality: { min: 0, max: 1 },
    position: 'top',
    fields: [
      { key: 'headline', label: 'Nagłówek', type: 'text', maxLength: 90 },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
    ],
    defaultContent: { headline: 'Pomoc całą dobę. Zadzwoń teraz.' },
  },
  {
    id: 'hero.editorial',
    category: 'hero',
    component: 'HeroEditorial',
    name: 'Hero edytorski',
    description: 'Cytat / duża typografia — styl magazynowy.',
    archetypes: ['home', 'home-editorial', 'about', 'landing'],
    cardinality: { min: 0, max: 1 },
    position: 'top',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'headline', label: 'Nagłówek', type: 'text', maxLength: 120 },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 280, optional: true },
    ],
    defaultContent: { headline: 'Medycyna bliska człowiekowi.' },
  },

  /* ── trust (exclusive group: pick one) ── */
  {
    id: 'proof.consolidated',
    category: 'trust',
    component: 'ProofConsolidated',
    name: 'Pasek zaufania',
    description: 'Ocena Google + lata + pacjenci + zespół w jednym pasku.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    exclusiveGroup: 'trust',
    fields: [{ key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true }],
    defaultContent: { eyebrow: 'Zaufanie pacjentów' },
  },
  {
    id: 'proof.stats',
    category: 'trust',
    component: 'StatGrid',
    name: 'Siatka liczb',
    description: 'Cztery kluczowe liczby w siatce.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    exclusiveGroup: 'trust',
    fields: [{ key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true }],
    defaultContent: {},
  },
  {
    id: 'proof.logos',
    category: 'trust',
    component: 'LogoStrip',
    name: 'Pasek logo / partnerzy',
    description: 'Logotypy partnerów i certyfikatów.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 80, optional: true }],
    defaultContent: {},
  },
  {
    id: 'proof.accreditations',
    category: 'trust',
    component: 'AccreditationsGrid',
    name: 'Akredytacje',
    description: 'Certyfikaty i członkostwa w siatce kart.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'accreditations',
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 80, optional: true }],
    defaultContent: { heading: 'Akredytacje i członkostwa' },
  },

  /* ── services ── */
  {
    id: 'services.grid',
    category: 'services',
    component: 'ServicesGridCards',
    name: 'Usługi — siatka kart',
    description: 'Usługi jako karty w siatce 2–4 kolumn.',
    archetypes: 'all',
    cardinality: { min: 0, max: 2 },
    dataBinding: 'services',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
      COLS_FIELD,
      { key: 'limit', label: 'Maks. liczba usług', type: 'number', min: 1, max: 12, optional: true },
    ],
    defaultContent: { eyebrow: 'Co robimy', heading: 'Pełen zakres opieki w jednym miejscu', cols: '3', limit: 6 },
  },
  {
    id: 'services.list',
    category: 'services',
    component: 'ServicesIndexList',
    name: 'Usługi — lista',
    description: 'Usługi jako czytelna lista z opisami.',
    archetypes: 'all',
    cardinality: { min: 0, max: 2 },
    dataBinding: 'services',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
    ],
    defaultContent: { heading: 'Nasze usługi' },
  },
  {
    id: 'services.prices',
    category: 'services',
    component: 'ServicesWithPrices',
    name: 'Cennik usług',
    description: 'Usługi z cenami i czasem trwania.',
    archetypes: 'all',
    cardinality: { min: 0, max: 2 },
    dataBinding: 'services',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
    ],
    defaultContent: { heading: 'Cennik' },
  },
  {
    id: 'services.accordion',
    category: 'services',
    component: 'ServicesAccordionByCategory',
    name: 'Usługi — akordeon',
    description: 'Usługi pogrupowane w rozwijane kategorie.',
    archetypes: 'all',
    cardinality: { min: 0, max: 2 },
    dataBinding: 'services',
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true }],
    defaultContent: { heading: 'Zakres usług' },
  },

  /* ── team (photo gate) ── */
  {
    id: 'team.grid',
    category: 'team',
    component: 'TeamGridPortraits',
    name: 'Zespół — portrety',
    description: 'Siatka zdjęć lekarzy. Ukrywa się przy <2 zdjęciach.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'doctors',
    minRequirements: { doctorPhotos: 2 },
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
      COLS_FIELD,
    ],
    defaultContent: { eyebrow: 'Twój zespół', heading: 'Doświadczenie i empatia', cols: '4' },
  },
  {
    id: 'team.bios',
    category: 'team',
    component: 'TeamWithBios',
    name: 'Zespół — z biogramami',
    description: 'Lekarze z dłuższymi opisami i specjalizacjami.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'doctors',
    minRequirements: { doctorPhotos: 1 },
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true }],
    defaultContent: { heading: 'Poznaj nasz zespół' },
  },

  /* ── reviews ── */
  {
    id: 'reviews.cards',
    category: 'reviews',
    component: 'ReviewsCards',
    name: 'Opinie — karty',
    description: 'Opinie pacjentów jako karty + agregat oceny.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'reviews',
    minRequirements: { reviews: 1 },
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
    ],
    defaultContent: { eyebrow: 'Pacjenci o nas' },
  },
  {
    id: 'reviews.featured',
    category: 'reviews',
    component: 'ReviewsSingleFeatured',
    name: 'Opinia wyróżniona',
    description: 'Jedna mocna opinia jako duży cytat.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'reviews',
    minRequirements: { reviews: 1 },
    fields: [{ key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true }],
    defaultContent: {},
  },
  {
    id: 'reviews.aggregate',
    category: 'reviews',
    component: 'ReviewsRatingAggregate',
    name: 'Ocena zbiorcza',
    description: 'Sama średnia ocena Google jako pasek.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    exclusiveGroup: 'trust',
    dataBinding: 'reviews',
    fields: [],
    defaultContent: {},
  },

  /* ── about ── */
  {
    id: 'about.story',
    category: 'about',
    component: 'AboutStoryBlock',
    name: 'O nas — historia',
    description: 'Tekst o klinice + zdjęcie.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
      { key: 'body', label: 'Treść', type: 'textarea', maxLength: 800, optional: true },
      { key: 'mediaSrc', label: 'Zdjęcie', type: 'image', optional: true },
    ],
    defaultContent: { eyebrow: 'O nas', heading: 'Klinika, która rozumie.' },
  },
  {
    id: 'about.mission',
    category: 'about',
    component: 'AboutMission',
    name: 'O nas — misja',
    description: 'Misja / filozofia kliniki.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    fields: [
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
      { key: 'body', label: 'Treść', type: 'textarea', maxLength: 800, optional: true },
    ],
    defaultContent: { heading: 'Nasza misja' },
  },

  /* ── contact ── */
  {
    id: 'contact.map',
    category: 'contact',
    component: 'ContactBlockMap',
    name: 'Kontakt z mapą',
    description: 'Adres, godziny, telefon + mapa Google.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'hours',
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
    ],
    defaultContent: { eyebrow: 'Lokalizacja', heading: 'Odwiedź nas' },
  },
  {
    id: 'contact.lead-form',
    category: 'contact',
    component: 'ContactWithLeadForm',
    name: 'Kontakt z formularzem',
    description: 'Dane kontaktowe + formularz zapytania.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true }],
    defaultContent: { heading: 'Napisz do nas' },
  },
  {
    id: 'contact.hours',
    category: 'contact',
    component: 'ContactBlockHours',
    name: 'Godziny otwarcia',
    description: 'Tabela godzin + telefon.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'hours',
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true }],
    defaultContent: { heading: 'Godziny otwarcia' },
  },

  /* ── cta (one conversion surface per page — audit rule) ── */
  {
    id: 'cta.final',
    category: 'cta',
    component: 'CtaFinal',
    name: 'CTA końcowe',
    description: 'Mocne wezwanie do umówienia wizyty.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    fields: [
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90 },
      { key: 'lead', label: 'Podtytuł', type: 'textarea', maxLength: 200, optional: true },
      { key: 'primaryCtaLabel', label: 'Przycisk', type: 'text', maxLength: 30, optional: true },
      { key: 'primaryCtaHref', label: 'Link', type: 'url', maxLength: 200, optional: true },
    ],
    defaultContent: { heading: 'Zarezerwuj pierwszą wizytę', lead: 'Wybierz dogodny termin online. Bez kolejek, bez czekania.', primaryCtaLabel: 'Umów wizytę', primaryCtaHref: '/umow' },
  },
  {
    id: 'cta.banner',
    category: 'cta',
    component: 'CtaBanner',
    name: 'CTA — baner',
    description: 'Poziomy baner z jednym przyciskiem.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    fields: [
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90 },
      { key: 'lead', label: 'Podtytuł', type: 'textarea', maxLength: 200, optional: true },
      { key: 'primaryCtaLabel', label: 'Przycisk', type: 'text', maxLength: 30, optional: true },
      { key: 'primaryCtaHref', label: 'Link', type: 'url', maxLength: 200, optional: true },
    ],
    defaultContent: { heading: 'Masz pytania? Jesteśmy do dyspozycji.', primaryCtaLabel: 'Skontaktuj się', primaryCtaHref: '#kontakt' },
  },

  /* ── booking ── */
  {
    id: 'booking.live',
    category: 'booking',
    component: 'BookingLiveWidget',
    name: 'Rezerwacja online',
    description: 'Pełny widget rezerwacji (lekarz, dzień, godzina).',
    archetypes: ['home', 'home-boutique', 'home-editorial', 'booking', 'landing'],
    cardinality: { min: 0, max: 1 },
    dataBinding: 'doctors',
    minRequirements: { doctors: 1 },
    fields: [
      { key: 'eyebrow', label: 'Nadtytuł', type: 'text', maxLength: 60, optional: true },
      { key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true },
      { key: 'lead', label: 'Wprowadzenie', type: 'textarea', maxLength: 240, optional: true },
    ],
    defaultContent: { eyebrow: 'Umów online', heading: 'Wybierz wolny termin' },
  },

  /* ── editorial (article pages) ── */
  {
    id: 'editorial.body',
    category: 'editorial',
    component: 'ArticleBody',
    name: 'Treść artykułu',
    description: 'Sformatowana treść (akapity, listy, obrazy).',
    archetypes: ['article', 'landing'],
    cardinality: { min: 0, max: 3 },
    fields: [{ key: 'bodyHtml', label: 'Treść (HTML)', type: 'textarea', maxLength: 20000, optional: true }],
    defaultContent: {},
  },
  {
    id: 'editorial.faq',
    category: 'editorial',
    component: 'ArticleFAQ',
    name: 'FAQ',
    description: 'Najczęstsze pytania jako rozwijana lista.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    dataBinding: 'faq',
    fields: [{ key: 'heading', label: 'Nagłówek', type: 'text', maxLength: 90, optional: true }],
    defaultContent: { heading: 'Najczęstsze pytania' },
  },

  /* ── conversion ── */
  {
    id: 'conversion.sticky-cta',
    category: 'conversion',
    component: 'StickyMobileCta',
    name: 'Przyklejony pasek CTA (mobile)',
    description: 'Dolny pasek z telefonem i przyciskiem na telefonach.',
    archetypes: 'all',
    cardinality: { min: 0, max: 1 },
    position: 'bottom',
    fields: [
      { key: 'primaryLabel', label: 'Przycisk', type: 'text', maxLength: 20, optional: true },
      { key: 'primaryHref', label: 'Link', type: 'url', maxLength: 200, optional: true },
    ],
    defaultContent: { primaryLabel: 'Umów', primaryHref: '/umow' },
  },

  /* ── layout (DUPLO-max nesting) ── */
  {
    id: 'layout.columns',
    category: 'layout',
    component: 'ColumnsContainer',
    name: 'Kolumny',
    description: 'Kontener: ustaw dwa lub więcej bloków obok siebie. Można zagnieżdżać.',
    archetypes: 'all',
    cardinality: { min: 0, max: 6 },
    // DUPLO-max: containers accept content blocks AND other containers
    // (bounded by MAX_BLOCK_DEPTH) so columns can nest one level.
    acceptsChildren: ['about', 'trust', 'services', 'reviews', 'contact', 'editorial', 'cta', 'layout'],
    fields: [COLS_FIELD],
    defaultContent: { cols: '2' },
  },
];

/** Fast lookup by id. */
export const BLOCK_BY_ID: Record<string, BlockManifestEntry> = Object.fromEntries(
  BLOCK_MANIFEST.map((b) => [b.id, b]),
);

export const BLOCK_TYPE_IDS = BLOCK_MANIFEST.map((b) => b.id);

/** Max nesting levels for container blocks (DUPLO is chunky, not infinite).
 * 3 levels = depth 0 (top) → 1 (columns) → 2 (content). A block landing at
 * depth ≥ 3 is rejected (`too_deep`) and the editor hides its add-nested
 * button. */
export const MAX_BLOCK_DEPTH = 3;

/* ───────────────────────────── instance schema ──────────────────────── */

/**
 * Typed condition DSL. No arbitrary code — only kinds the static-R2 model
 * can honor (build-time predicates + CSS-expressible viewport gates).
 */
export const ConditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('until'), date: z.string() }),
  z.object({ kind: z.literal('from'), date: z.string() }),
  z.object({
    kind: z.literal('min-count'),
    field: z.enum(['services', 'doctors', 'reviews']),
    n: z.number().int().nonnegative(),
  }),
  z.object({ kind: z.literal('viewport'), max: z.number().int().positive().optional(), min: z.number().int().positive().optional() }),
]);
export type Condition = z.infer<typeof ConditionSchema>;

/** Counts available to condition evaluation (from the brief fixture). */
export interface ConditionContext {
  now: number; // epoch ms — pass build time; keeps evaluation deterministic/testable
  counts: { services: number; doctors: number; reviews: number };
}

/**
 * Evaluate whether a block survives to the rendered output at BUILD time.
 *
 *   until / from   → date predicates against ctx.now (the page is rebuilt on
 *                    a schedule when a boundary is crossed — see CLAUDE.md
 *                    "conditional content" tiers).
 *   min-count      → require N services/doctors/reviews in the brief.
 *   viewport       → NOT a build-time drop; rendered always and gated with a
 *                    CSS responsive-hide class by the renderer. Returns true.
 *
 * A malformed date fails open (block shown) rather than silently vanishing.
 */
export function conditionPassesAtBuild(cond: Condition | undefined, ctx: ConditionContext): boolean {
  if (!cond) return true;
  switch (cond.kind) {
    case 'until': {
      const t = Date.parse(`${cond.date}T23:59:59`);
      return Number.isNaN(t) ? true : ctx.now <= t;
    }
    case 'from': {
      const t = Date.parse(`${cond.date}T00:00:00`);
      return Number.isNaN(t) ? true : ctx.now >= t;
    }
    case 'min-count':
      return (ctx.counts[cond.field] ?? 0) >= cond.n;
    case 'viewport':
      return true;
  }
}

export interface BlockInstance {
  id: string;
  type: string;
  variant?: string;
  /** Filled slots — validated against the manifest field descriptors. */
  content?: Record<string, unknown>;
  /** Layout knobs (all enum/bool, never raw style). */
  layout?: { cols?: number; bleed?: boolean; tone?: 'default' | 'surface' | 'dark' | 'accent' };
  /** 'brief' = auto-bound to clinic data; 'inline' = content-only. */
  source?: 'brief' | 'inline';
  condition?: Condition;
  /** Container children — only for blocks whose manifest has acceptsChildren. */
  children?: BlockInstance[];
}

export const BlockInstanceSchema: z.ZodType<BlockInstance> = z.lazy(() =>
  z.object({
    id: z.string().min(1).max(40),
    type: z.string().min(1).max(60),
    variant: z.string().max(40).optional(),
    content: z.record(z.unknown()).optional(),
    layout: z
      .object({
        cols: z.number().int().min(1).max(4).optional(),
        bleed: z.boolean().optional(),
        tone: z.enum(['default', 'surface', 'dark', 'accent']).optional(),
      })
      .optional(),
    source: z.enum(['brief', 'inline']).optional(),
    condition: ConditionSchema.optional(),
    children: z.array(BlockInstanceSchema).max(8).optional(),
  }),
);

export const BlocksSchema = z.array(BlockInstanceSchema).max(40);

/* ──────────────────────────── validation ────────────────────────────── */

export interface CompositionIssue {
  level: 'error' | 'warning';
  code: string;
  message: string; // Polish — surfaced in editor
  blockId?: string;
}

function flatten(blocks: BlockInstance[]): BlockInstance[] {
  const out: BlockInstance[] = [];
  const walk = (list: BlockInstance[]) => {
    for (const b of list) {
      out.push(b);
      if (b.children?.length) walk(b.children);
    }
  };
  walk(blocks);
  return out;
}

function archetypeAllows(entry: BlockManifestEntry, archetype: PageArchetype): boolean {
  return entry.archetypes === 'all' || entry.archetypes.includes(archetype);
}

/**
 * The single source of truth for "what arrangements are legal". Runs in the
 * admin editor (live, blocks illegal actions) AND at build time (defense in
 * depth). Returns errors (block the save) + warnings (allow, but flag, e.g.
 * the photo gate — block renders empty until data arrives).
 */
export function validateComposition(
  blocks: BlockInstance[],
  archetype: PageArchetype,
): CompositionIssue[] {
  const issues: CompositionIssue[] = [];
  const all = flatten(blocks);

  // 1. Every block type known + allowed for this archetype.
  for (const b of all) {
    const entry = BLOCK_BY_ID[b.type];
    if (!entry) {
      issues.push({ level: 'error', code: 'unknown_block', message: `Nieznany blok: ${b.type}`, blockId: b.id });
      continue;
    }
    if (!archetypeAllows(entry, archetype)) {
      issues.push({
        level: 'error',
        code: 'archetype_not_allowed',
        message: `Blok „${entry.name}" nie jest dostępny na tej stronie.`,
        blockId: b.id,
      });
    }
  }

  // 2a. Per-block-type cardinality (count of the exact type). Handles the
  //    distinct structural slots (one nav, one footer) and "services can
  //    repeat up to N" cases.
  const byCategory = new Map<BlockCategory, BlockInstance[]>();
  const typeCounts = new Map<string, number>();
  for (const b of all) {
    const entry = BLOCK_BY_ID[b.type];
    if (!entry) continue;
    const arr = byCategory.get(entry.category) ?? [];
    arr.push(b);
    byCategory.set(entry.category, arr);
    typeCounts.set(b.type, (typeCounts.get(b.type) ?? 0) + 1);
  }
  for (const [type, count] of typeCounts) {
    const entry = BLOCK_BY_ID[type];
    if (entry && count > entry.cardinality.max) {
      issues.push({
        level: 'error',
        code: 'too_many',
        message: `Za dużo bloków „${entry.name}" (maks. ${entry.cardinality.max}).`,
      });
    }
  }

  // 2b. Singleton categories — at most one block of the whole category, ANY
  //    variant. Encodes the audit rules: exactly one hero, one CTA surface
  //    per page (booking-inhouse > znanylekarz > phone > lead-form picked
  //    deterministically — never two CTAs stacked).
  const SINGLETON_CATEGORIES = new Set<BlockCategory>(['hero', 'cta']);
  for (const cat of SINGLETON_CATEGORIES) {
    const c = (byCategory.get(cat) ?? []).length;
    if (c > 1) {
      issues.push({
        level: 'error',
        code: 'too_many',
        message:
          cat === 'cta'
            ? 'Tylko jedno wezwanie do działania (CTA) na stronie — wybierz jedno.'
            : 'Tylko jeden nagłówek (hero) na stronie — wybierz jeden.',
      });
    }
  }

  // Required structural blocks (nav + footer) must be present exactly once.
  for (const entry of BLOCK_MANIFEST.filter((e) => e.cardinality.min >= 1)) {
    const count = all.filter((b) => b.type === entry.id).length;
    if (count < entry.cardinality.min) {
      issues.push({ level: 'error', code: 'missing_required', message: `Brak wymaganego bloku: „${entry.name}".` });
    }
  }

  // 3. Exclusive groups — at most one per group (no stacked trust strips).
  const groupCounts = new Map<string, number>();
  for (const b of all) {
    const g = BLOCK_BY_ID[b.type]?.exclusiveGroup;
    if (g) groupCounts.set(g, (groupCounts.get(g) ?? 0) + 1);
  }
  for (const [g, n] of groupCounts) {
    if (n > 1) {
      issues.push({
        level: 'error',
        code: 'exclusive_conflict',
        message:
          g === 'trust'
            ? 'Wybierz jeden blok zaufania — nie układaj kilku pasków oceny obok siebie.'
            : `Bloki z grupy „${g}" wykluczają się — zostaw jeden.`,
      });
    }
  }

  // 4. Position rules — 'top' blocks first, 'bottom' blocks last among the
  //    top-level content stack (ignoring locked structure for the check).
  const top = blocks; // position rules apply to the top-level stack only
  top.forEach((b, i) => {
    const entry = BLOCK_BY_ID[b.type];
    if (!entry || entry.locked) return;
    if (entry.position === 'top') {
      const earlierContent = top.slice(0, i).filter((x) => {
        const e = BLOCK_BY_ID[x.type];
        return e && !e.locked && e.category !== 'structure';
      });
      if (earlierContent.length > 0) {
        issues.push({
          level: 'error',
          code: 'must_be_top',
          message: `Blok „${entry.name}" musi być na samej górze strony.`,
          blockId: b.id,
        });
      }
    }
  });

  // 5. Nesting — children must be accepted by their container; depth cap.
  const checkChildren = (list: BlockInstance[], depth: number, parent?: BlockManifestEntry) => {
    for (const b of list) {
      const entry = BLOCK_BY_ID[b.type];
      if (b.children?.length) {
        if (!entry?.acceptsChildren) {
          issues.push({ level: 'error', code: 'not_a_container', message: `Blok „${entry?.name ?? b.type}" nie może zawierać innych bloków.`, blockId: b.id });
        }
        if (depth + 1 >= MAX_BLOCK_DEPTH) {
          issues.push({ level: 'error', code: 'too_deep', message: 'Za głębokie zagnieżdżenie bloków.', blockId: b.id });
        }
        if (entry?.acceptsChildren) {
          for (const child of b.children) {
            const ce = BLOCK_BY_ID[child.type];
            if (ce && !entry.acceptsChildren.includes(ce.category)) {
              issues.push({ level: 'error', code: 'child_not_allowed', message: `Blok „${ce.name}" nie pasuje do kontenera „${entry.name}".`, blockId: child.id });
            }
          }
        }
        checkChildren(b.children, depth + 1, entry);
      }
      void parent;
    }
  };
  checkChildren(blocks, 0);

  // 6. minRequirements → warnings (block renders empty / hidden until met).
  for (const b of all) {
    const entry = BLOCK_BY_ID[b.type];
    if (!entry?.minRequirements) continue;
    if (entry.minRequirements.doctorPhotos) {
      issues.push({
        level: 'warning',
        code: 'needs_photos',
        message: `Blok „${entry.name}" pokaże się dopiero przy min. ${entry.minRequirements.doctorPhotos} zdjęciach zespołu.`,
        blockId: b.id,
      });
    }
    if (entry.minRequirements.reviews) {
      issues.push({
        level: 'warning',
        code: 'needs_reviews',
        message: `Blok „${entry.name}" wymaga co najmniej ${entry.minRequirements.reviews} opinii.`,
        blockId: b.id,
      });
    }
  }

  return issues;
}

/** Convenience: errors only (build gate). */
export function compositionErrors(blocks: BlockInstance[], archetype: PageArchetype): CompositionIssue[] {
  return validateComposition(blocks, archetype).filter((i) => i.level === 'error');
}

/* ───────────────────── archetype preset block stacks ────────────────── */

const inst = (type: string, content?: Record<string, unknown>): BlockInstance => {
  const entry = BLOCK_BY_ID[type];
  const id = `${type.replace(/[^a-z0-9]+/gi, '-')}-${Math.abs(hashish(type + JSON.stringify(content ?? {})))}`;
  return { id, type, content: { ...(entry?.defaultContent ?? {}), ...(content ?? {}) }, source: 'brief' };
};

// Tiny deterministic hash for seed ids (Math.random is unavailable in some
// runtimes here and we want stable ids for diff-friendliness).
function hashish(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

/**
 * The default editable block stack for an archetype. Used to (a) seed a
 * newly created custom page and (b) expand a legacy archetype-only page
 * into an editable tree the first time the customer opens the composer.
 * Mirrors the hardcoded ClinicHomePage composition so behaviour is
 * unchanged until the customer edits.
 */
export function defaultBlocksForArchetype(archetype: PageArchetype): BlockInstance[] {
  const nav = inst('nav.standard');
  const footer = inst('footer.standard');
  const sticky = inst('conversion.sticky-cta');

  const content = ((): BlockInstance[] => {
    switch (archetype) {
      case 'home':
      case 'home-boutique':
      case 'home-editorial':
        return [
          inst('hero.media-split'),
          inst('proof.consolidated'),
          inst('services.grid'),
          inst('team.grid'),
          inst('reviews.cards'),
          inst('about.story'),
          inst('booking.live'),
          inst('contact.map'),
          inst('cta.final'),
        ];
      case 'about':
        return [inst('hero.editorial'), inst('about.story'), inst('team.bios'), inst('proof.accreditations')];
      case 'contact':
        return [inst('hero.centered'), inst('contact.map'), inst('contact.lead-form')];
      case 'reviews':
        return [inst('hero.centered'), inst('reviews.aggregate'), inst('reviews.cards')];
      case 'landing':
        return [inst('hero.media-split'), inst('proof.consolidated'), inst('services.grid'), inst('cta.final')];
      case 'emergency':
        return [inst('hero.emergency'), inst('contact.map')];
      case 'booking':
        return [inst('hero.centered'), inst('booking.live'), inst('editorial.faq')];
      case 'article':
        return [inst('editorial.body'), inst('editorial.faq')];
      case 'procedure':
        return [inst('hero.media-split'), inst('services.grid'), inst('editorial.faq'), inst('cta.final')];
      default:
        return [inst('hero.media-split'), inst('cta.final')];
    }
  })();

  return [nav, ...content, footer, sticky];
}
